"use client"
import {useState, useEffect, useCallback, ReactNode} from "react"
import AppIcon from "@/app/components/atomic/AppIcon"

interface Props {
    items: ReactNode[]
    dotActiveColor?: string
    lgPerPage?: number
}

function usePerPage(lgPerPage: number): number {
    const [perPage, setPerPage] = useState(1)

    const update = useCallback(() => {
        setPerPage(window.matchMedia("(min-width: 1024px)").matches ? lgPerPage : 1)
    }, [lgPerPage])

    useEffect(() => {
        update()
        const mql = window.matchMedia("(min-width: 1024px)")
        mql.addEventListener("change", update)
        return () => mql.removeEventListener("change", update)
    }, [update])

    return perPage
}

export default function Carousel({items, dotActiveColor = "bg-pastel-green", lgPerPage = 1}: Props) {
    const perPage = usePerPage(lgPerPage)
    const pageCount = Math.ceil(items.length / perPage)
    const [activePage, setActivePage] = useState(0)

    useEffect(() => {
        setActivePage((prev) => Math.min(prev, pageCount - 1))
    }, [pageCount])

    const goTo = (index: number) => {
        if (index < 0) setActivePage(pageCount - 1)
        else if (index >= pageCount) setActivePage(0)
        else setActivePage(index)
    }

    if (items.length === 0) return null

    const start = activePage * perPage
    const visibleItems = items.slice(start, start + perPage)

    return (
        <div className="flex flex-col items-center gap-8" role="group" aria-roledescription="carousel">
            <div
                className={`w-full ${perPage > 1 ? "grid gap-4" : ""}`}
                style={perPage > 1 ? {gridTemplateColumns: `repeat(${perPage}, minmax(0, 1fr))`} : undefined}
                aria-live="polite"
                aria-atomic="true"
            >
                {visibleItems.map((item, i) => (
                    <div key={start + i}>{item}</div>
                ))}
            </div>

            {pageCount > 1 && (
                <div className="flex items-center gap-2">
                    <button
                        onClick={() => goTo(activePage - 1)}
                        className="w-8 h-8 flex items-center justify-center rounded-full text-rm-gray hover:text-pale"
                        aria-label="Précédent"
                    >
                        <AppIcon iconClassName="fa-chevron-left" size="lg" type="regular" />
                    </button>

                    <div className="flex items-center">
                        {Array.from({length: pageCount}, (_, index) => (
                            <button
                                key={index}
                                onClick={() => setActivePage(index)}
                                aria-label={`Page ${index + 1}`}
                                aria-current={index === activePage ? "true" : undefined}
                                className="min-w-6 h-6 px-[3px] flex items-center justify-center"
                            >
                                <span
                                    className={`block rounded-full transition-all duration-300 ${
                                        index === activePage
                                            ? `w-8 h-2.5 ${dotActiveColor}`
                                            : "w-2.5 h-2.5 bg-gray-300"
                                    }`}
                                />
                            </button>
                        ))}
                    </div>

                    <button
                        onClick={() => goTo(activePage + 1)}
                        className="w-8 h-8 flex items-center justify-center rounded-full text-rm-gray hover:text-pale"
                        aria-label="Suivant"
                    >
                        <AppIcon iconClassName="fa-chevron-right" size="lg" type="regular" />
                    </button>
                </div>
            )}
        </div>
    )
}
